import { useMarkPostsRead } from '@repo/api/post/useMarkPostsRead';
import { useEffect, useRef } from 'react';

const POST_ID_ATTRIBUTE = 'data-post-id';
const VISIBLE_SHARE = 0.65;
const FLUSH_DELAY_MS = 1000;
const MAX_BATCH = 50;
// Fire at every 5% step so posts taller than the viewport still report while scrolling through them.
const THRESHOLDS: number[] = Array.from({ length: 21 }, (_, i) => i / 20);

// Posts already reported in this session, shared by every list so remounts don't resend them.
const reportedPostIds = new Set<number>();

/**
 * A post counts as read once 65% of it is on screen, or, for a post taller than
 * the viewport, once it fills 65% of the viewport.
 */
function isRead(entry: IntersectionObserverEntry): boolean {
  if (!entry.isIntersecting) {
    return false;
  }
  const viewportHeight = entry.rootBounds?.height ?? window.innerHeight;
  return entry.intersectionRatio >= VISIBLE_SHARE || entry.intersectionRect.height >= viewportHeight * VISIBLE_SHARE;
}

interface UsePostReadTrackerOptions {
  /** Ids of the rendered posts, used to pick up newly rendered post elements. */
  postIds: number[];
  /** Whether reads should be reported. */
  enabled: boolean;
}

/**
 * Reports posts as read when they are scrolled into view. Bind `containerRef` to an
 * element wrapping the posts, each post element carrying `data-post-id`.
 */
export function usePostReadTracker(options: UsePostReadTrackerOptions) {
  const { postIds, enabled } = options;
  const { mutate: markPostsRead } = useMarkPostsRead();

  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingRef = useRef<Set<number>>(new Set());
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markPostsReadRef = useRef(markPostsRead);

  useEffect(() => {
    markPostsReadRef.current = markPostsRead;
  }, [markPostsRead]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const pending = pendingRef.current;

    const flush = () => {
      flushTimeoutRef.current = null;
      const ids = Array.from(pending);
      pending.clear();
      for (let i = 0; i < ids.length; i += MAX_BATCH) {
        const batch = ids.slice(i, i + MAX_BATCH);
        markPostsReadRef.current(batch, {
          // Allow a retry the next time the post is seen.
          onError: () => batch.forEach((id) => reportedPostIds.delete(id)),
        });
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!isRead(entry)) {
            return;
          }
          const postId = Number(entry.target.getAttribute(POST_ID_ATTRIBUTE));
          observer.unobserve(entry.target);
          if (Number.isNaN(postId) || reportedPostIds.has(postId)) {
            return;
          }
          reportedPostIds.add(postId);
          pending.add(postId);
          flushTimeoutRef.current ??= setTimeout(flush, FLUSH_DELAY_MS);
        });
      },
      { root: null, threshold: THRESHOLDS }
    );

    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
      // Send what was seen right before leaving the page.
      flush();
    };
  }, [enabled]);

  useEffect(() => {
    const observer = observerRef.current;
    if (!enabled || !observer || !containerRef.current) {
      return;
    }

    containerRef.current.querySelectorAll(`[${POST_ID_ATTRIBUTE}]`).forEach((element) => {
      if (!reportedPostIds.has(Number(element.getAttribute(POST_ID_ATTRIBUTE)))) {
        observer.observe(element);
      }
    });
  }, [postIds, enabled]);

  return { containerRef };
}
